/**
 * M2 audio feature (order `FEATURE_ORDER.m2Audio`, after the audio feature; M2 plan §2.12, M2-21): career mode
 * only. It never touches the audio feature: it reads the running engine, score and comms from the audio feature's
 * default export and, each frame the engine runs, routes the new space events and the tool readouts through
 * src/audio/m2/route.ts: tool, salvage and docking sounds, the tool loops, the fracture and ownership stingers,
 * the station radio while docked, and spoken clearance and lease notices.
 *
 * The `m2` service (the career runtime, M2-22) is looked up at init and, if it is not there yet, once per frame
 * until it appears (a service lookup, no allocation). The station voices follow the stations runtime: when its
 * `version` moves (a Reach system switch replaced the list, M3 R22e) the router takes the new stations' comms seeds.
 * `frame` allocates nothing but what the engine and comms accept per sound or line, and that reseed (D-014; see
 * src/audio/engine.ts).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import type { CommsSystem } from '../../audio/comms'
import type { AudioEngine } from '../../audio/engine'
import { applyMusicCues, createM2AudioRouter, routeReadouts, routeSpaceEvents, skipSpaceEvents, stopM2Loops, type M2AudioRouter, type M2MusicSink, type M2Readouts } from '../../audio/m2/route'
import { sfxConfig, type SfxConfig } from '../../audio/sfxSchema'
import { logger } from '../../core/logger'
import type { M2Runtime } from '../../space/contracts'
import { Life } from '../../world/components'
import audioFeature from '../audio/feature'

/** What the feature needs from the audio feature (its default export; a fake in tests). */
export interface AudioHost {
  readonly engine: AudioEngine | null
  readonly music: M2MusicSink | null
  readonly comms: Pick<CommsSystem, 'say'> | null
}

export interface M2AudioDeps {
  audio: AudioHost
  config: SfxConfig
}

export interface M2AudioFeature extends GameFeature {
  /** The router once the `m2` service is bound (tests and the dev panel). */
  readonly router: M2AudioRouter | null
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/** The feature's mutable state (module scratch for the hot path). */
interface M2AudioState {
  readonly audio: AudioHost
  readonly config: SfxConfig
  active: boolean
  m2: M2Runtime | null
  router: M2AudioRouter | null
  readouts: M2Readouts | null
  /** The stations runtime's `version` the router's station seeds were read at. */
  stationsVersion: number
  /** The M2 loops may be sounding (stop them once when the engine pauses or the feature stops). */
  loopsOn: boolean
}

function onCommsFailed(err: unknown): void {
  logger.warn('m2 audio: comms line failed', err)
}

/** Binds the career runtime: the router (station voices) and the readouts the loops follow. Once per career. */
function bind(st: M2AudioState, m2: M2Runtime): void {
  const space = m2.space
  st.m2 = m2
  st.router = createM2AudioRouter(st.config, stationSeeds(m2))
  st.stationsVersion = space.stations.version ?? 0
  st.readouts = { mining: space.rocks.readout, salvage: space.salvage.readout, scan: space.tools.scan, docking: space.docking.readout }
  // Start at the ring head: events from before the audio came up are not replayed.
  skipSpaceEvents(st.router, space.events)
}

/** The comms seed of every current station, in index order (`M2Data.stations`, which a system switch replaces). */
function stationSeeds(m2: M2Runtime): number[] {
  return m2.data.stations.stations.map((s) => s.commsSeed)
}

/** The station list was replaced (M3 R22e): the same router state with the new stations' voices. Rare; allocates. */
function reseed(st: M2AudioState, m2: M2Runtime, r: M2AudioRouter): M2AudioRouter {
  const next = { ...r, stationSeeds: Int32Array.from(stationSeeds(m2)) }
  st.router = next
  st.stationsVersion = m2.space.stations.version ?? 0
  return next
}

/** Speaks the queued comms lines (each `say` renders a short line offline; event-driven, not per frame). */
function speak(st: M2AudioState, r: M2AudioRouter): void {
  const comms = st.audio.comms
  if (comms !== null) for (let i = 0; i < r.commsCount; i++) comms.say(r.commsSeed[i], r.commsStress[i]).catch(onCommsFailed)
  r.commsCount = 0
}

/** One frame of M2 audio. Allocation-free (the engine's one source node per sound excepted). */
function frameM2Audio(st: M2AudioState, ctx: GameContext, f: FrameInfo): void {
  if (!st.active) return
  if (st.m2 === null) {
    const found = ctx.services.maybe('m2')
    if (found === undefined) return
    bind(st, found)
  }
  const m2 = st.m2
  const bound = st.router
  const ro = st.readouts
  if (m2 === null || bound === null || ro === null) return
  const r = (m2.space.stations.version ?? 0) === st.stationsVersion ? bound : reseed(st, m2, bound)
  const ring = m2.space.events
  const e = st.audio.engine
  if (e === null || f.paused) {
    skipSpaceEvents(r, ring)
    r.commsCount = 0
    if (e !== null && st.loopsOn) stopM2Loops(e)
    st.loopsOn = false
    return
  }
  const p = ctx.sim.playerEid
  const alive = p >= 0 && Life.alive[p] === 1
  routeSpaceEvents(r, ring, p, e)
  routeReadouts(r, ro, alive, e)
  st.loopsOn = true
  applyMusicCues(r, st.audio.music)
  speak(st, r)
}

export function createM2AudioFeature(deps: Partial<M2AudioDeps> = {}): M2AudioFeature {
  const st: M2AudioState = {
    audio: deps.audio ?? audioFeature,
    config: deps.config ?? sfxConfig,
    active: false,
    m2: null,
    router: null,
    readouts: null,
    stationsVersion: 0,
    loopsOn: false,
  }
  return {
    name: 'm2audio',
    order: FEATURE_ORDER.m2Audio,
    get router(): M2AudioRouter | null {
      return st.router
    },
    init(ctx: GameContext): void {
      st.active = ctx.mode === 'career'
      if (!st.active) return
      const m2 = ctx.services.maybe('m2')
      if (m2 !== undefined) bind(st, m2)
    },
    frame(ctx: GameContext, f: FrameInfo): void {
      frameM2Audio(st, ctx, f)
    },
    dispose(): void {
      const e = st.audio.engine
      if (e !== null && st.loopsOn) stopM2Loops(e)
      st.active = false
      st.loopsOn = false
      st.m2 = null
      st.router = null
      st.readouts = null
    },
  }
}

const feature: M2AudioFeature = createM2AudioFeature()

export default feature
